import { Injectable } from '@angular/core';
import { CustomerSelectionOption } from '@app/customer/models/customer-selection-option.model';
import { CustomerSelectionVM } from '@app/customer/models/customer-selection-vm.model';
import { getCustomers } from '@app/ngrx-customer/store';
import { ModalController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { PlatformEnum, PlatformService } from '@panamax/app-state';
import { CustomerSelectionPaymentsFilterMobileComponent } from '@shared/components/header/customer-selection/customer-selection-payments-filter-mobile/customer-selection-payments-filter-mobile.component';
import { CustomerSelectionPaymentsTouchComponent } from '@shared/components/header/customer-selection/customer-selection-payments-touch/customer-selection-payments-touch.component';
import { ModalWithNavComponent } from '@shared/components/modal-with-nav/modal-with-nav.component';
import { UsfFilteringModalDesktopComponent } from '@shared/components/usf-filtering-modal/usf-filtering-modal-desktop/usf-filtering-modal-desktop.component';
import { UsfFilteringModalMobileComponent } from '@shared/components/usf-filtering-modal/usf-filtering-modal-mobile/usf-filtering-modal-mobile.component';
import { UsfFilteringDatesOptionComponent } from '@shared/components/usf-filtering-modal/usf-filtering-options/usf-filtering-dates-option/usf-filtering-dates-option.component';
import { UsfSortOptionsComponent } from '@shared/components/usf-filtering-modal/usf-filtering-options/usf-sort-options/usf-sort-options.component';
import { ModalService } from '@shared/services/modal/modal.service';
import dayjs from 'dayjs';
import { BehaviorSubject, combineLatest, map, Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CustomerReportingModalService {
  private searchText$ = new BehaviorSubject<string>('');
  public filtersSelected = new Subject<any>();

  constructor(
    private platformService: PlatformService,
    private store: Store,
    private modalService: ModalService,
    private modalController: ModalController,
  ) {}

  public getCustomerSelectionVM$(): Observable<CustomerSelectionVM> {
    return combineLatest([
      this.store.select(getCustomers),
      this.searchText$,
    ]).pipe(
      map(([customers, searchText]) => {
        const isDesktop =
          this.platformService.getPlatformType() === PlatformEnum.desktop;
        const customerRowHeight = isDesktop ? 54 : 58;
        const customerSelectionOptions: CustomerSelectionOption[] = [];
        const rowHeights: number[] = [];

        customers
          .filter(
            customer =>
              customer.customerName
                .toLowerCase()
                .includes(searchText.toLowerCase()) ||
              customer?.customerNumber
                .toString()
                .includes(searchText.toLowerCase()),
          )
          .forEach(customer => {
            const customerInfo: CustomerSelectionOption =
              this.createCustomerSelectionOption(customer);
            customerSelectionOptions.push(customerInfo);
            rowHeights.push(customerRowHeight);
          });

        return {
          customerSelectionOptions,
          rowHeights,
          platformType: this.platformService.platformType,
          showAllCustomersOption: true,
        };
      }),
    );
  }

  async open(
    fromDate: number,
    toDate: number,
    selectedCustomers: CustomerSelectionOption[],
    quickDate: boolean = false,
  ) {
    const isTouch = this.platformService.isTouch.value;
    const isMobile = this.platformService.platformType === PlatformEnum.mobile;

    const modalComponent = isMobile
      ? UsfFilteringModalMobileComponent
      : UsfFilteringModalDesktopComponent;

    this.modalService.setModalOptions(
      isTouch,
      ModalWithNavComponent,
      {
        rootPage: modalComponent,
        rootPageParams: {
          isTouch,
          cancelButton: true,
          isBusinessAnalytics: true,
          disableSort: true,
          selectedFilterIndex: quickDate ? 0 : null,
          sortComponent: {
            component: UsfSortOptionsComponent,
            filterName: 'sortBy',
            data: {
              platformType: this.platformService.platformType,
              title: 'Sort By',
              sortOptions: [],
              sortSelection: [],
            },
          },
          title: 'Filter',
          filters: [
            {
              filterName: 'date',
              component: UsfFilteringDatesOptionComponent,
              data: {
                title: 'Date Range',
                subTitle$: new BehaviorSubject(
                  this.formattedDateLabel(fromDate, toDate),
                ),
                fromDate,
                toDate,
                maxDate: dayjs(new Date()).subtract(1, 'days').toDate(),
                minDate: dayjs(new Date())
                  .subtract(2, 'year')
                  .startOf('year')
                  .toDate(),
              },
            },
            {
              filterName: 'customers',
              component:
                this.platformService.platformType === PlatformEnum.mobile
                  ? CustomerSelectionPaymentsFilterMobileComponent
                  : CustomerSelectionPaymentsTouchComponent,
              data: {
                title: 'Customer',
                subTitle$: new BehaviorSubject(''),
                vm$: this.getCustomerSelectionVM$(),
                searchText$: this.searchText$,
                show: true,
                isPresentedInFilterModal: false,
                selectedCustomers,
              },
            },
          ],
        },
      },
      'usf-filtering-modal',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );

    modal.onDidDismiss().then(event => {
      this.handleDismiss(event);
    });

    await modal.present();
  }

  handleDismiss(event) {
    this.filtersSelected.next(event);
  }

  private formattedDateLabel(fromDate: number, toDate: number) {
    return (
      this.formattedDateFromNumber(fromDate) +
      ' - ' +
      this.formattedDateFromNumber(toDate)
    );
  }

  private formattedDateFromNumber(date: number) {
    if (!!date) {
      const dateString = date.toString();
      return (
        dateString.substring(4, 6) +
        '/' +
        dateString.substring(6, 8) +
        '/' +
        dateString.substring(0, 4)
      );
    }
    return 'MM/DD/YYYY';
  }

  createCustomerSelectionOption(
    customer,
    isDefaultCustomer: boolean = false,
  ): CustomerSelectionOption {
    const title =
      customer.customerName +
      (customer?.customerNumber === 0 ? '' : ` (${customer?.customerNumber})`);

    const alphabets = title.match('[a-zA-Z]{1}');
    let customerInfo: CustomerSelectionOption = {
      title,
      subtitle: '',
      firstLetter: !!alphabets?.length ? alphabets[0] : '',
      customerName: customer.customerName,
      customerNumber: customer?.customerNumber,
      divisionNumber: customer?.divisionNumber,
      departmentNumber: '0',
      hasDepartments: customer.departments?.length ? true : false,
      isDepartment: false,
      isSelected: isDefaultCustomer,
    };

    return customerInfo;
  }
}
